import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { BehaviorSubject, forkJoin, map, Observable } from "rxjs";
import { PaginatedData } from "../shared/model/table.interface";
import {
  ChangeExcludedFromSignaturesParams,
  CurrentUserResponse,
  Employee,
  EmployeeListItem,
  EmployeePickerItem,
  EmployeePickerSearchParams,
  EmployeeListSearchParams,
  EmployeeRequest,
  SingleEmployeeResponse,
} from "./employees.interface";

@Injectable({
  providedIn: "root",
})
export class EmployeesService {
  private refreshEmployeesSubject = new BehaviorSubject<void>(undefined);
  public refreshEmployees$ = this.refreshEmployeesSubject.asObservable();

  constructor(private http: HttpClient) {}

  triggerRefreshEmployees(): void {
    this.refreshEmployeesSubject.next();
  }

  getEmployees(
    { pager, ...params }: EmployeeListSearchParams,
    includeAliases: boolean = false,
  ): Observable<PaginatedData<EmployeeListItem>> {
    const httpParams: Record<string, string | string[]> = {
      page: String(pager.page),
      limit: String(pager.limit),
      ...params,
    };

    if (includeAliases) {
      httpParams.aliases = "true";
    }

    return this.http.get<PaginatedData<EmployeeListItem>>("/employees", {
      params: httpParams,
    });
  }

  getEmployeesPicker({
    pager,
    ...params
  }: EmployeePickerSearchParams): Observable<
    PaginatedData<EmployeePickerItem>
  > {
    const httpParams: Record<string, string | string[]> = {
      page: String(pager.page),
      limit: String(pager.limit),
      ...params,
    };

    return this.http.get<PaginatedData<EmployeePickerItem>>(
      "/employees/picker",
      {
        params: httpParams,
      },
    );
  }

  getCombinedEmployees({
    pager,
    ...params
  }: EmployeeListSearchParams): Observable<PaginatedData<EmployeeListItem>> {
    const httpParams: Record<string, string | string[]> = {
      page: String(pager.page),
      limit: String(pager.limit),
      ...params,
    };

    return this.http.get<PaginatedData<EmployeeListItem>>(
      "/employees/combined",
      {
        params: httpParams,
      },
    );
  }

  getAllEmployees(): Observable<Employee[]> {
    return this.http.get<Employee[]>("/employees/all");
  }

  getAllEmployeesForManager(): Observable<Employee[]> {
    return this.http.get<Employee[]>("/employees/manager");
  }

  getAllEmployeesWithAliases(): Observable<
    { employee: EmployeeListItem; aliases: EmployeeListItem[] }[]
  > {
    const employees$ =
      this.http.get<PaginatedData<EmployeeListItem>>("/employees");
    const aliases$ = this.http.get<PaginatedData<EmployeeListItem>>(
      "/employees",
      {
        params: { aliases: "true" },
      },
    );

    return forkJoin([employees$, aliases$]).pipe(
      map(([employeesResponse, aliasesResponse]) => {
        const employees = employeesResponse.data;
        const aliases = aliasesResponse.data.map((alias) => ({
          ...alias,
          isAlias: true,
        }));

        return employees.map((employee) => {
          return {
            employee,
            aliases: aliases.filter(
              (alias) => alias.fullName === employee.fullName,
            ),
          };
        });
      }),
    );
  }

  excludeAllEmployees(
    filters: EmployeeListSearchParams,
    exclude: boolean,
  ): Observable<void> {
    return this.http.post<void>(`/employees/exclude-all`, { filters, exclude });
  }

  getNotExcludedEmployees(): Observable<Employee[]> {
    return this.http.get<Employee[]>("/employees/not-excluded");
  }

  getNotExcludedEmployeesWithAliases(): Observable<Employee[]> {
    return this.http.get<Employee[]>("/employees/not-excluded-with-aliases");
  }

  changeExcludedFromSignatures(
    payload: ChangeExcludedFromSignaturesParams,
  ): Observable<unknown> {
    return this.http.put(`/employees/${payload.employeeId}`, {
      excludedFromSignatures: payload.excludedFromSignatures,
    });
  }

  sendEmployeeRequest(payload: EmployeeRequest): Observable<unknown> {
    return this.http.post(`/employees/invite`, payload);
  }

  getEmployee(employeeId: string): Observable<SingleEmployeeResponse> {
    return this.http.get<SingleEmployeeResponse>(`/employees/${employeeId}`);
  }

  createEmployee(payload: Employee): Observable<unknown> {
    return this.http.post(`/employees`, payload);
  }

  updateEmployee(payload: Employee): Observable<unknown> {
    return this.http.put(`/employees/${payload.employeeId}`, payload);
  }

  batchUpdateEmployees(
    employees: Array<{ email: string; data: Partial<Employee> }>,
    installSignature: boolean,
  ): Observable<{
    updated: number;
    errors: Array<{ email: string; error: string }>;
  }> {
    return this.http.put<{
      updated: number;
      errors: Array<{ email: string; error: string }>;
    }>(`/employees/batch-update`, { employees, installSignature });
  }

  updateEmployeeAndInstallSignature(payload: Employee): Observable<unknown> {
    return this.http.put(`/user`, payload);
  }

  updateCurrentUser(payload: Employee): Observable<unknown> {
    return this.http.put(`/user/update`, payload);
  }

  removeSignatureTemplate(
    employeeId: string | undefined,
    email: string | undefined,
    isAlias: boolean,
    internal: boolean = false,
  ): Observable<void> {
    const pathId = isAlias ? email : employeeId;
    let params = new HttpParams().set("isAlias", isAlias.toString());
    if (internal) {
      params = params.set("internal", "true");
    }
    return this.http.delete<void>(`/employees/${pathId}/signature-remove`, {
      params,
    });
  }

  deleteEmployee(employeeId: string): Observable<void> {
    return this.http.delete<void>(`/employees/${employeeId}`);
  }

  checkSyncLock(): Observable<{ lock: boolean; currentSyncUser?: string }> {
    return this.http.get<{ lock: boolean; currentSyncUser?: string }>(
      `/google-sync-lock`,
    );
  }

  syncGoogleEmployees(): Observable<{
    success: boolean;
    error?: { type: string; message?: string };
    googleSyncStateId?: string;
  }> {
    return this.http.post<{
      success: boolean;
      error?: { type: string; message?: string };
      googleSyncStateId?: string;
    }>("/google-employees-sync", {});
  }

  syncMicrosoftEmployees(): Observable<{
    success: boolean;
    error?: { type: string; message?: string };
    microsoftSyncStateId?: string;
  }> {
    return this.http.post<{
      success: boolean;
      error?: { type: string; message?: string };
      microsoftSyncStateId?: string;
    }>("/microsoft-employees-sync", {});
  }

  syncEmployee(
    employeeId: string,
  ): Observable<{ success: boolean; error?: unknown }> {
    return this.http.post<{
      success: boolean;
      error?: unknown;
    }>(`/employees/${employeeId}/sync`, {});
  }

  checkMicrosoftSyncLock(): Observable<{
    lock: boolean;
    currentSyncUser?: string;
  }> {
    return this.http.get<{ lock: boolean; currentSyncUser?: string }>(
      `/microsoft-sync-lock`,
    );
  }

  getCurrentUser(): Observable<CurrentUserResponse> {
    return this.http.get<CurrentUserResponse>(`/user`);
  }

  downloadAllSignatures(token?: string): Observable<Blob> {
    const headers = token ? { "x-api-token": token } : {};
    return this.http.get("/employees/signatures/download", {
      headers,
      responseType: "blob",
    });
  }
}
